import { useQuery } from '@tanstack/react-query'
import { clubsRepository } from '../api/clubs-repository'

export const clubKeys = {
  detail: (clubId: string) => ['club', clubId] as const,
  subscription: (clubId: string) => ['club', clubId, 'subscription'] as const,
  features: (clubId: string) => ['club', clubId, 'features'] as const,
}

function isPermissionDenied(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'permission-denied'
}

/**
 * Retry policy for the club doc read. The ClubGate preview-readiness probe
 * (`usePreviewClubReady`) keys off this query: right after `redeem_preview_token`
 * signs the visitor in, the Firestore client can briefly still use the
 * pre-claim token → `permission-denied`. The global `retry: 1` (~1s) didn't
 * cover token propagation on slower loads, so the probe errored, the gate
 * surrendered, and the dashboard's `onSnapshot` listeners mounted into the race
 * and DIED (snapshot permission errors are terminal — no auto-retry). Retry
 * `permission-denied` generously so the gate WAITS through propagation (resolves
 * in ~1–3s); other errors keep the single default retry. After the cap it falls
 * through to the gate's error path (no infinite splash).
 */
export function shouldRetryClubQuery(failureCount: number, err: unknown): boolean {
  return isPermissionDenied(err) ? failureCount < 8 : failureCount < 1
}

export function useClub(clubId: string | null) {
  return useQuery({
    queryKey: clubId ? clubKeys.detail(clubId) : ['club', 'none'],
    queryFn: () => clubsRepository.getById(clubId!),
    enabled: !!clubId,
    staleTime: 60_000,
    retry: shouldRetryClubQuery,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
  })
}

export function useClubSubscription(clubId: string | null) {
  return useQuery({
    queryKey: clubId ? clubKeys.subscription(clubId) : ['club', 'none', 'subscription'],
    queryFn: () => clubsRepository.getSubscription(clubId!),
    enabled: !!clubId,
    staleTime: 60_000,
  })
}

export function useClubFeatures(clubId: string | null) {
  return useQuery({
    queryKey: clubId ? clubKeys.features(clubId) : ['club', 'none', 'features'],
    queryFn: () => clubsRepository.getFeatures(clubId!),
    enabled: !!clubId,
    staleTime: 5 * 60_000,
  })
}
